import { Fragment, useEffect, useState } from "react";
import { useQuery, useQueries } from "@tanstack/react-query";
import { useDispatch, useSelector } from "react-redux";
import { Box, Grid, Typography, Divider, Button, IconButton, Hidden } from "@mui/material";
import { BASE_INFOS_API, FAMILY_INFORMATION_API, FILTER_API } from "../../api";
import {
  addNewFamilyMember,
  updateFamilyInformationData,
  setFamilyInformationData,
  editFamilyMember,
  editFamilyMemberRelation,
  deleteFamilyMember,
  setReloadFamilyInformationData,
} from "../../slice";
import { useFamilyInfo } from "../../hooks";
import AddField from "./AddField";
import Input from "../Input";
import Select from "../Select";
import { HiOutlineTrash } from "react-icons/hi";
import BaseTextArea from "../all-inputs/BaseTextArea";
import { BaseRadioGroup } from "../inputs";

function FamilyInformation({
  hasEditAccess,
  disabled = false,
  onError = () => {},
  pageErrors = {},
}) {
  const dispatch = useDispatch();
  const { data, familyMembers, reloadPage } = useSelector((state) => state.FamilyInformation);
  const [errors, setErrors] = useState({});
  const { handleSubmit: handleSubmitForm } = useFamilyInfo({ hasEditAccess, errors });

  const handleMaritalStatusChange = (event, index) => {
    dispatch(
      editFamilyMember({
        index,
        fieldName: "maritalStatus",
        value: event.target.value,
      })
    );
    if (event.target.value !== "36002") {
      dispatch(
        editFamilyMember({
          index,
          fieldName: "spouseEducationTypeCode",
          value: undefined,
        })
      );
      dispatch(
        editFamilyMember({
          index,
          fieldName: "spouseJob",
          value: undefined,
        })
      );
    }
  };

  const { data: lookupsData } = useQuery(
    ["getFilterDataCollection_familyinfo"],
    () =>
      FILTER_API.GetFilterDataCollection(false, [28000, 10000, 11000, 29000, 34000, 35000, 36000]),
    {
      staleTime: Infinity,
    }
  );

  //todo: in bayad be useQuery tabdil beshe
  const [provinces] = useQueries({
    queries: [
      {
        queryKey: ["getProvincesByCountryId", 1],
        queryFn: BASE_INFOS_API.GetProvincesByCountryId.bind(null, 1),
        staleTime: Infinity,
      },
    ],
  });

  const { refetch: getFamilyInformation } = useQuery(
    ["getFamilyInformation"],
    () => FAMILY_INFORMATION_API.GetFamilyInformation(),
    {
      onSuccess: ({ data }) => {
        if (data.success && data.data) {
          dispatch(setFamilyInformationData(data.data));
          dispatch(setReloadFamilyInformationData(false));
        }
      },

      staleTime: Infinity,
    }
  );

  useEffect(() => {
    if (reloadPage) getFamilyInformation();
  }, [reloadPage]);

  return (
    <form style={{ width: "100%" }}>
      <Box display="flex" flexDirection="column" width="100%" alignItems="flex-end">
        <Typography variant="h1" mb="1rem">
          وضعیت کلی خانواده
        </Typography>
        <Grid container flexDirection="row" sx={{ width: "100%", maxWidth: "800px" }}>
          <Grid item xs={12} sm={6} md={6} lg={6} sx={Style.itemGrid}>
            <Select
              label="وضعیت تاهل و زندگی والدین"
              data={lookupsData?.data?.data[10000]}
              idKey="code"
              labelKey="name"
              labelFlex={{ xs: 5, sm: 5, md: 6, lg: 6 }}
              flex={7}
              name="livingConditionParents"
              value={data.livingConditionParentsCode}
              onChange={(event) =>
                dispatch(
                  updateFamilyInformationData({
                    key: "livingConditionParentsCode",
                    value: event.target.value,
                    required: true,
                  })
                )
              }
              disabled={disabled}
              error={"livingConditionParentsCode" in pageErrors}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={6} sx={Style.itemGrid}>
            <Select
              label="وضعیت اعتقادی خانواده"
              data={lookupsData?.data?.data[28000]}
              idKey="code"
              labelKey="name"
              labelFlex={5}
              flex={7}
              name="beliefTypeCode"
              value={data.beliefTypeCode}
              onChange={(event) =>
                dispatch(
                  updateFamilyInformationData({
                    key: "beliefTypeCode",
                    value: event.target.value,
                    required: true,
                  })
                )
              }
              disabled={disabled}
              error={"beliefTypeCode" in pageErrors}
            />
          </Grid>
        </Grid>
        <Grid container flexDirection="row-reverse" sx={{ width: "100%", maxWidth: "800px" }}>
          <Grid item xs={12} sm={6} md={6} lg={6} sx={Style.itemGrid}>
            <Select
              label="وضعیت اقتصادی"
              data={lookupsData?.data?.data[11000]}
              idKey="code"
              labelKey="name"
              labelFlex={5}
              flex={7}
              name="economicLevel"
              value={data.economicLevelCode}
              onChange={(event) =>
                dispatch(
                  updateFamilyInformationData({
                    key: "economicLevelCode",
                    value: event.target.value,
                    required: true,
                  })
                )
              }
              disabled={disabled}
              error={"economicLevelCode" in pageErrors}
            />
          </Grid>
          <BaseRadioGroup
            label="وضعیت سکونت"
            value={data.residenceTypeCode}
            onChange={(event) =>
              dispatch(
                updateFamilyInformationData({
                  key: "residenceTypeCode",
                  value: event.target.value,
                  required: true,
                })
              )
            }
            disabled={disabled}
            data={lookupsData?.data?.data[34000]}
            containerGridSize={{
              xs: 12,
              display: "flex",
              flexDirection: "row",
              justifyContent: "flex-end",
              alignItems: "flex-start",
              alignContent: "flex-end",
            }}
            labelGridSize={{ xs: 4.75, sm: 2.25, display: "flex", flexDirection: "column-reverse" }}
            variant="h2"
            error={pageErrors["residenceTypeCode"]}
          />
          <Grid item xs={12} sx={Style.itemGrid}>
            <BaseTextArea
              label="توضیحات"
              value={data.economicDescription}
              name="economicDescription"
              labelFlex={{ xs: 5, sm: 1.75, md: 2.5, lg: 2.5 }}
              flex={{ xs: 6.7, sm: 6.7, md: 9.65, lg: 9.6 }}
              onChange={(event) =>
                dispatch(
                  updateFamilyInformationData({
                    key: "economicDescription",
                    value: event.target.value,
                    required: true,
                  })
                )
              }
              disabled={disabled}
            />
          </Grid>
        </Grid>
        <Grid container flexDirection="row-reverse" sx={{ width: "100%", maxWidth: "800px" }}>
          <Grid item xs={12} sm={6} md={6} lg={6} sx={Style.itemGrid}>
            <Select
              label="وضعیت حیات پدر"
              data={lookupsData?.data?.data[29000]}
              idKey="code"
              labelKey="name"
              name="fatherLifeType"
              value={data.fatherLifeTypeCode}
              labelFlex={5}
              flex={7}
              onChange={(event) =>
                dispatch(
                  updateFamilyInformationData({
                    key: "fatherLifeTypeCode",
                    value: event.target.value,
                    required: true,
                  })
                )
              }
              disabled={disabled}
              error={"fatherLifeTypeCode" in pageErrors}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={6} sx={Style.itemGrid}>
            <Select
              label="وضعیت حیات مادر"
              data={lookupsData?.data?.data[29000]}
              idKey="code"
              labelKey="name"
              name="motherLifeType"
              value={data.motherLifeTypeCode}
              labelFlex={5}
              flex={7}
              onChange={(event) =>
                dispatch(
                  updateFamilyInformationData({
                    key: "motherLifeTypeCode",
                    value: event.target.value,
                    required: true,
                  })
                )
              }
              disabled={disabled}
              error={"motherLifeTypeCode" in pageErrors}
            />
          </Grid>
        </Grid>
        <Box mt="1.3rem" mb="2rem" width="100%">
          <Divider />
        </Box>
        <Typography
          variant="h1"
          mb="0.2rem"
          sx={{
            direction: "rtl",
            display: { xs: "block", md: "inline-block" },
          }}
        >
          اطلاعات اعضای خانواده
        </Typography>
        <Typography
          variant="h3"
          mb="1rem"
          sx={{
            direction: "rtl",
          }}
        >
          (وارد کردن اطلاعات خواهرها و برادرها ضروری است.)
        </Typography>
        <Grid
          container
          flexDirection="row-reverse"
          justifyContent="space-between"
          alignContent="flex-end"
          columnSpacing={2}
          alignItems="center"
          pr={2.6}
          pl={2}
          sx={{
            display: { xs: "none", lg: "flex" },
            boxSizing: "border-box",
          }}
        >
          <Grid item xs={1}>
            <Typography variant="h2" textAlign="right">
              نسبت
            </Typography>
          </Grid>
          <Grid item xs={1}>
            <Typography variant="h2" textAlign="right" sx={{ direction: "rtl" }}>
              سال تولد (‌اختیاری)
            </Typography>
          </Grid>
          <Grid item xs={2}>
            <Typography variant="h2" textAlign="right">
              محل تولد
            </Typography>
          </Grid>
          <Grid item xs={1.3}>
            <Typography variant="h2" textAlign="right">
              شغل
            </Typography>
          </Grid>
          <Grid item xs={1.7}>
            <Typography variant="h2" textAlign="right">
              تحصیلات
            </Typography>
          </Grid>
          <Grid item xs={1.3}>
            <Typography variant="h2" textAlign="right">
              وضعیت تاهل
            </Typography>
          </Grid>
          <Grid item xs={1.7}>
            <Typography variant="h2" textAlign="right">
              تحصیلات همسر
            </Typography>
          </Grid>
          <Grid item xs={1.4}>
            <Typography variant="h2" textAlign="right">
              شغل همسر
            </Typography>
          </Grid>
          <Grid item xs={0.6}></Grid>
        </Grid>
        {data.familyMembers.map((member, index) => (
          <Fragment key={index}>
            <Grid
              container
              width="100%"
              flexDirection="row-reverse"
              justifyContent="flex-start"
              alignContent="flex-end"
              columnSpacing={2}
              alignItems="center"
              sx={{
                boxSizing: "border-box",
              }}
              border="1px solid #dbdae2"
              borderRadius="16px"
              py={1}
              pr={2}
              my="0.375rem"
              key={index}
            >
              {member.relation < 3 ? (
                <Grid item xs={12} sm={12} lg={1} mb={{ xs: 1, md: 0 }} pr={0.2}>
                  <Typography variant="h2">
                    {member.relation === 1 && "پدر"}
                    {member.relation === 2 && "مادر"}
                  </Typography>
                </Grid>
              ) : (
                <Grid item xs={12} sm={6} lg={1} mb={{ xs: 1, md: 0 }}>
                  <Select
                    label={<Hidden lgUp>نسبت</Hidden>}
                    data={familyMembers}
                    idKey="relation"
                    labelKey="label"
                    value={member.relation}
                    onChange={(event) =>
                      dispatch(
                        editFamilyMemberRelation({
                          index,
                          relation: event.target.value,
                        })
                      )
                    }
                    flex={{ xs: 7, lg: 1 }}
                    labelFlex={{ xs: 5, lg: 0 }}
                    disabled={disabled}
                    haveEmptyOption={false}
                    error={`relation_${index + 1}` in pageErrors}
                  />
                </Grid>
              )}
              <Grid item xs={12} sm={6} lg={1} mb={{ xs: 1, lg: 0 }}>
                <Input
                  label={<Hidden lgUp>سال تولد (اختیاری)</Hidden>}
                  type="number"
                  validationType="year"
                  value={member.birthDate}
                  id={`birthDate${index}`}
                  name={`birthDate${index}`}
                  onChange={(event) =>
                    dispatch(
                      editFamilyMember({
                        index,
                        fieldName: "birthDate",
                        value: event.target.value,
                      })
                    )
                  }
                  flex={{ xs: 7, lg: 1 }}
                  labelFlex={{ xs: 5, lg: 0 }}
                  disabled={disabled}
                  onErrorMessage={(error_message) => {
                    setErrors({ ...errors, ...error_message });
                    onError({ ...errors, ...error_message });
                  }}
                  error={`birthDate_${index + 1}` in pageErrors}
                />
              </Grid>
              <Grid item xs={12} sm={6} lg={2} mb={{ xs: 1, md: 0 }}>
                <Select
                  label={<Hidden lgUp>محل تولد</Hidden>}
                  data={provinces?.data?.data.data}
                  idKey="id"
                  labelKey="name"
                  value={member.birthProvinceId}
                  onChange={(event) =>
                    dispatch(
                      editFamilyMember({
                        index,
                        fieldName: "birthProvinceId",
                        value: event.target.value,
                      })
                    )
                  }
                  flex={{ xs: 7, lg: 1 }}
                  labelFlex={{ xs: 5, lg: 0 }}
                  disabled={disabled}
                  error={`birthProvinceId_${index + 1}` in pageErrors}
                />
              </Grid>
              <Grid item xs={12} sm={6} lg={1.3} mb={{ xs: 1, lg: 0 }}>
                <Input
                  label={<Hidden lgUp>شغل</Hidden>}
                  value={member.job}
                  onChange={(event) =>
                    dispatch(
                      editFamilyMember({
                        index,
                        fieldName: "job",
                        value: event.target.value,
                      })
                    )
                  }
                  flex={{ xs: 7, lg: 1 }}
                  labelFlex={{ xs: 5, lg: 0 }}
                  disabled={disabled}
                  error={`job_${index + 1}` in pageErrors}
                />
              </Grid>
              <Grid item xs={12} sm={6} lg={1.7} mb={{ xs: 1, lg: 0 }}>
                <Select
                  label={<Hidden lgUp>تحصیلات</Hidden>}
                  data={lookupsData?.data?.data[35000]}
                  idKey="code"
                  labelKey="name"
                  value={member?.educationTypeCode}
                  onChange={(event) =>
                    dispatch(
                      editFamilyMember({
                        index,
                        fieldName: "educationTypeCode",
                        value: event.target.value,
                      })
                    )
                  }
                  flex={{ xs: 7, lg: 1 }}
                  labelFlex={{ xs: 5, lg: 0 }}
                  disabled={disabled}
                  error={`educationTypeCode_${index + 1}` in pageErrors}
                />
              </Grid>
              <Grid item xs={12} sm={6} lg={1.3} mb={{ xs: 1, md: 0 }}>
                {member.relation > 2 && (
                  <Select
                    label={<Hidden lgUp>وضعیت تاهل</Hidden>}
                    data={lookupsData?.data?.data[36000]}
                    idKey="code"
                    labelKey="name"
                    value={member.maritalStatus}
                    onChange={(event) => handleMaritalStatusChange(event, index)}
                    flex={{ xs: 7, lg: 1 }}
                    labelFlex={{ xs: 5, lg: 0 }}
                    disabled={disabled}
                    error={`maritalStatus_${index + 1}` in pageErrors}
                  />
                )}
              </Grid>
              {member.relation > 2 ? (
                <>
                  <Grid item xs={12} sm={6} lg={1.7} my={1}>
                    <Select
                      label={<Hidden lgUp>تحصیلات همسر</Hidden>}
                      data={lookupsData?.data?.data[35000]}
                      labelKey="name"
                      idKey="code"
                      value={
                        parseInt(member?.maritalStatus) !== 36002
                          ? 0
                          : member?.spouseEducationTypeCode
                          ? member?.spouseEducationTypeCode
                          : 0
                      }
                      onChange={(event) =>
                        dispatch(
                          editFamilyMember({
                            index,
                            fieldName: "spouseEducationTypeCode",
                            value: event.target.value,
                          })
                        )
                      }
                      flex={{ xs: 7, lg: 1 }}
                      labelFlex={{ xs: 5, lg: 0 }}
                      disabled={disabled || parseInt(member?.maritalStatus) !== 36002}
                      error={`spouseEducationTypeCode_${index + 1}` in pageErrors}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} lg={1.4} my={1}>
                    <Input
                      label={<Hidden lgUp>شغل همسر</Hidden>}
                      value={
                        parseInt(member?.maritalStatus) !== 36002
                          ? ""
                          : member?.spouseJob
                          ? member?.spouseJob
                          : ""
                      }
                      onChange={(event) =>
                        dispatch(
                          editFamilyMember({
                            index,
                            fieldName: "spouseJob",
                            value: event.target.value,
                          })
                        )
                      }
                      flex={{ xs: 7, lg: 1 }}
                      labelFlex={{ xs: 5, lg: 0 }}
                      disabled={disabled || parseInt(member?.maritalStatus) !== 36002}
                      error={`spouseJob_${index + 1}` in pageErrors}
                    />
                  </Grid>
                  <Grid item xs={12} sm={12} lg={0.6} my={1}>
                    <IconButton
                      sx={{
                        backgroundColor: "#EAEAEA",
                        borderRadius: "0.6rem",
                        marginRight: "1.5rem",
                        width: "40px",
                        height: "40px",
                        alignSelf: "center",
                      }}
                      disabled={disabled}
                      onClick={() => dispatch(deleteFamilyMember(index))}
                    >
                      <HiOutlineTrash />
                    </IconButton>
                  </Grid>
                </>
              ) : (
                <>
                  <Grid item md={1.7} mt={1} sx={{ display: { xs: "none", md: "flex" } }}></Grid>

                  <Grid item md={1.4} mt={1} sx={{ display: { xs: "none", md: "flex" } }}></Grid>

                  <Grid item md={0.6} mt={1} sx={{ display: { xs: "none", md: "flex" } }}></Grid>
                </>
              )}
            </Grid>
          </Fragment>
        ))}
        {!disabled && (
          <Fragment key={1}>
            <AddField
              handleAdd={() => dispatch(addNewFamilyMember())}
              title="اضافه کردن خواهر / برادر "
            />
          </Fragment>
        )}
        <Grid container flexDirection="row-reverse">
          <Grid item xs={12}>
            <BaseTextArea
              labelFlex={{ xs: 2, sm: 2, md: 1, lg: 1.145, xl: 1.06 }}
              flex={{ xs: 2.6, sm: 10, md: 10, lg: 10.2 }}
              label="توضیحات"
              value={data.description}
              name="description"
              onChange={(event) =>
                dispatch(
                  updateFamilyInformationData({
                    key: "description",
                    value: event.target.value,
                    required: true,
                  })
                )
              }
              disabled={disabled}
            />
          </Grid>
        </Grid>
        <Box
          width="100%"
          sx={{
            display: "flex",
            flexDirection: { xs: "row", sm: "row", md: "row" },
            justifyContent: "center",
            alignItems: "center",
            pt: 2,
            mt: "2rem",
          }}
        >
          <Button
            type="button"
            variant="m1"
            onClick={() => handleSubmitForm("/personal-info/request")}
            sx={{ mr: 1, width: "165px", my: { xs: 1, sm: 0, md: 0 } }}
          >
            بعدی
          </Button>
          <Button
            type="button"
            variant="m2"
            onClick={() => handleSubmitForm("/personal-info/financial-info")}
            sx={{ mr: 1 }}
          >
            قبلی
          </Button>
        </Box>
      </Box>
    </form>
  );
}

const Style = {
  itemGrid: {
    boxSizing: "border-box",
    paddingLeft: { xs: 0, sm: "0.5rem", md: "1rem", lg: "1rem" },
    marginBottom: "0.75rem",
  },
};

export default FamilyInformation;
